import React, { useEffect, useMemo, useState } from "react";
import { Plus, Save, Trash2, X } from "lucide-react";
import { supabase } from "../../../Services/supabaseClient";
import { useCompany } from "../../../context/CompanyContext";
import { fetchCompanyCOA } from "./coaFetch";
import "./fixedAssets.css";

export default function FixedAssetCategories() {
  const { companyId } = useCompany();

  const [coa, setCoa] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [form, setForm] = useState({
    name: "",
    default_useful_life_months: "36",
    default_residual_value: "0",
    default_asset_account_id: "",
    default_accum_dep_account_id: "",
    default_dep_expense_account_id: "",
  });

  function setField(k, v) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  const assetAccounts = useMemo(
    () => coa.filter((a) => a.type === "Asset"),
    [coa]
  );
  const expenseAccounts = useMemo(
    () => coa.filter((a) => a.type === "Expense"),
    [coa]
  );

  async function fetchAll() {
    if (!companyId) return;
    setLoading(true);

    const coaData = await fetchCompanyCOA(companyId);
    setCoa(coaData);

    const catRes = await supabase
      .from("fixed_asset_categories")
      .select("*")
      .eq("company_id", companyId)
      .order("name", { ascending: true });

    if (!catRes.error) setCats(catRes.data || []);

    setLoading(false);
  }

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  function openNew() {
    setEditing(null);
    setForm({
      name: "",
      default_useful_life_months: "36",
      default_residual_value: "0",
      default_asset_account_id: "",
      default_accum_dep_account_id: "",
      default_dep_expense_account_id: "",
    });
    setOpen(true);
  }

  function openEdit(cat) {
    setEditing(cat);
    setForm({
      name: cat.name || "",
      default_useful_life_months: String(cat.default_useful_life_months ?? 36),
      default_residual_value: String(cat.default_residual_value ?? 0),
      default_asset_account_id: cat.default_asset_account_id || "",
      default_accum_dep_account_id: cat.default_accum_dep_account_id || "",
      default_dep_expense_account_id: cat.default_dep_expense_account_id || "",
    });
    setOpen(true);
  }

  async function save() {
    if (!form.name.trim()) return alert("Category name is required.");

    const payload = {
      company_id: companyId,
      name: form.name.trim(),
      default_useful_life_months:
        Number(form.default_useful_life_months || 0) || null,
      default_residual_value: Number(form.default_residual_value || 0),
      default_asset_account_id: form.default_asset_account_id || null,
      default_accum_dep_account_id: form.default_accum_dep_account_id || null,
      default_dep_expense_account_id:
        form.default_dep_expense_account_id || null,
    };

    if (editing?.id) {
      const { error } = await supabase
        .from("fixed_asset_categories")
        .update(payload)
        .eq("company_id", companyId)
        .eq("id", editing.id);

      if (error) return alert(error.message);
    } else {
      const { error } = await supabase
        .from("fixed_asset_categories")
        .insert(payload);
      if (error) return alert(error.message);
    }

    setOpen(false);
    await fetchAll();
  }

  async function remove(id) {
    const yes = window.confirm("Delete this category?");
    if (!yes) return;

    const { error } = await supabase
      .from("fixed_asset_categories")
      .delete()
      .eq("company_id", companyId)
      .eq("id", id);

    if (error) return alert(error.message);
    fetchAll();
  }

  return (
    <div className="fa-wrap">
      <div className="fa-header">
        <div className="fa-title">
          <h2>Asset Categories</h2>
          <p>
            Set default accounts and useful life. Selecting a category
            auto-fills new asset setup.
          </p>
        </div>
        <div className="fa-actions">
          <button className="fa-btn fa-btn-primary" onClick={openNew}>
            <Plus size={16} /> New Category
          </button>
        </div>
      </div>

      <div className="fa-panel">
        {loading ? (
          <div style={{ opacity: 0.8, padding: 10 }}>Loading…</div>
        ) : (
          <table className="fa-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Default life (months)</th>
                <th>Residual</th>
                <th style={{ width: 160 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {cats.map((c) => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td>{c.default_useful_life_months ?? "-"}</td>
                  <td>
                    {Number(c.default_residual_value || 0).toLocaleString()}
                  </td>
                  <td>
                    <div className="fa-row-actions">
                      <button className="fa-btn" onClick={() => openEdit(c)}>
                        Edit
                      </button>
                      <button className="fa-btn" onClick={() => remove(c.id)}>
                        <Trash2 size={16} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {cats.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ opacity: 0.75 }}>
                    No categories yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {open && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.97)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 14,
            zIndex: 9999,
          }}
          onClick={() => setOpen(false)}
        >
          <div
            className="fa-panel"
            style={{ width: "min(820px, 100%)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 10,
              }}
            >
              <div>
                <div style={{ fontSize: 18, fontWeight: 900 }}>
                  {editing ? "Edit Category" : "New Category"}
                </div>
              </div>
              <button
                className="fa-icon-btn"
                onClick={() => setOpen(false)}
                title="Close"
              >
                <X size={16} />
              </button>
            </div>

            <div className="fa-toolbar" style={{ marginTop: 12 }}>
              <input
                className="fa-input"
                placeholder="Category name"
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
              />
              <input
                className="fa-input"
                type="number"
                placeholder="Default useful life (months)"
                value={form.default_useful_life_months}
                onChange={(e) =>
                  setField("default_useful_life_months", e.target.value)
                }
              />
              <input
                className="fa-input"
                type="number"
                placeholder="Default residual value"
                value={form.default_residual_value}
                onChange={(e) =>
                  setField("default_residual_value", e.target.value)
                }
              />
            </div>

            <div
              style={{
                marginTop: 10,
                fontSize: 12,
                opacity: 0.8,
                marginBottom: 6,
              }}
            >
              Default Fixed Asset Account (cost)
            </div>
            <select
              className="fa-select"
              value={form.default_asset_account_id}
              onChange={(e) =>
                setField("default_asset_account_id", e.target.value)
              }
              style={{ width: "100%" }}
            >
              <option value="">(optional)</option>
              {assetAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code ? `${a.code} - ` : ""}
                  {a.name} ({a.category})
                </option>
              ))}
            </select>

            <div
              style={{
                marginTop: 10,
                fontSize: 12,
                opacity: 0.8,
                marginBottom: 6,
              }}
            >
              Default Accumulated Depreciation Account
            </div>
            <select
              className="fa-select"
              value={form.default_accum_dep_account_id}
              onChange={(e) =>
                setField("default_accum_dep_account_id", e.target.value)
              }
              style={{ width: "100%" }}
            >
              <option value="">(optional)</option>
              {assetAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code ? `${a.code} - ` : ""}
                  {a.name} ({a.category})
                </option>
              ))}
            </select>

            <div
              style={{
                marginTop: 10,
                fontSize: 12,
                opacity: 0.8,
                marginBottom: 6,
              }}
            >
              Default Depreciation Expense Account
            </div>
            <select
              className="fa-select"
              value={form.default_dep_expense_account_id}
              onChange={(e) =>
                setField("default_dep_expense_account_id", e.target.value)
              }
              style={{ width: "100%" }}
            >
              <option value="">(optional)</option>
              {expenseAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code ? `${a.code} - ` : ""}
                  {a.name} ({a.category})
                </option>
              ))}
            </select>

            <div
              className="fa-actions"
              style={{ marginTop: 12, justifyContent: "flex-end" }}
            >
              <button className="fa-btn" onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button className="fa-btn fa-btn-primary" onClick={save}>
                <Save size={16} /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
